import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Addnewcostcenter from "./Addnewcostcenter";
import EditCostcenterModal from "./EditCostcenterModal";
import AssignCostcenter from "./AssignCostcenter";
const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    height: "8px",
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.5),
    },
  },
}));

function Costcenters() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [costcenters, setCostcenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCostcenter, setSelectedCostcenter] = useState(null);
  const [openAddNewCostcenter, setOpenAddNewCostcenter] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 5;
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  useEffect(() => {
    fetchCostcenters();
  }, []);

  const fetchCostcenters = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/costcenters");
      const data = await res.json();
      const list = data.recordset || data;
      setCostcenters(list);
    } catch (error) {
      console.error("Error fetching costcenters:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async ({ entity, unit, costcenter }) => {
    await fetch(`/api/support/costcenters/${selectedCostcenter.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity, unitName: unit, costcenter }),
    });
    fetchCostcenters();
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };
  const filteredCostcenters = costcenters.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.id?.toString().toLowerCase().includes(search) ||
      item.costcenter_name?.toLowerCase().includes(search) ||
      item.tinNumber?.toLowerCase().includes(search)
    );
  });

  const sortedCostcenters = [...filteredCostcenters].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedCostcenters.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedCostcenter(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCostcenter(null);
  };

  const handleAddNew = () => {
    setOpenAddNewCostcenter(true);
  };

  const handleEdit = () => {
    setEditModalOpen(true);
    setAnchorEl(null);
  };
  const handleAssign = () => {
    setAssignModalOpen(true);
    setAnchorEl(null);
  };
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: 3,
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Costcenters
        </Typography>

        {/* ✅ Search Input */}
        <TextField
          size="small"
          placeholder="Search by ID or Costcenter name or TIN..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          sx={{ minWidth: 280 }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* <Typography variant="body2" color="text.secondary">
            {filteredCostcenters.length}{" "}
            {filteredCostcenters.length === 1 ? "costcenter" : "costcenters"}{" "}
            found
          </Typography> */}

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add New
          </Button>
        </Box>
      </Box>

      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": {
                  color: "white",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                },
              }}
            >
              {[
                { id: "id", label: "ID" },
                { id: "costcenter_name", label: "Costcenter Name" },
                { id: "tinNumber", label: "TIN Number" },
                { id: "ifmisCode", label: "IFMIS Code" },
                { id: "Country", label: "Country" },
                { id: "actions", label: "Action" },
              ].map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() =>
                    column.id !== "actions" && handleSort(column.id)
                  }
                  sx={{
                    cursor: column.id !== "actions" ? "pointer" : "default",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    {column.label}
                  </Box>
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading Costcenters...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((item) => (
                <StyledTableRow key={item.id} hover>
                  <ModernTableCell>{item.id}</ModernTableCell>
                  <ModernTableCell>{item.costcenter_name}</ModernTableCell>
                  <ModernTableCell>{item.tinNumber}</ModernTableCell>
                  <ModernTableCell>{item.ifmisCode}</ModernTableCell>
                  <ModernTableCell>{item.Country}</ModernTableCell>
                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, item)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          "&:hover": {
                            backgroundColor: alpha(PRIMARY_COLOR, 0.1),
                          },
                        }}
                      >
                        <MoreVertIcon
                          fontSize={isMobile ? "small" : "medium"}
                        />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">No costcenters found</Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredCostcenters.length / itemsPerPage)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
          size={isMobile ? "small" : "medium"}
          sx={{
            "& .MuiPaginationItem-root": {
              "&.Mui-selected": {
                bgcolor: PRIMARY_COLOR,
                "&:hover": { bgcolor: SECONDARY_COLOR },
              },
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="body2">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleAssign}>
          <AssignmentIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="body2">Assign</Typography>
        </MenuItem>
      </Menu>
      <EditCostcenterModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCostcenter(null);
        }}
        onSaveSuccess={fetchCostcenters}
        onSave={handleSave}
        costcenter={selectedCostcenter}
      />
      <AssignCostcenter
        open={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setSelectedCostcenter(null);
        }}
        costcenter={selectedCostcenter}
        onSaveSuccess={fetchCostcenters}
      />
      <Addnewcostcenter
        open={openAddNewCostcenter}
        onClose={() => setOpenAddNewCostcenter(false)}
      />
    </Box>
  );
}

export default Costcenters;
